'use client';

import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useState,
  startTransition,
} from 'react';
import classNames from 'classnames';
import { useReducedMotion } from 'framer-motion';
import dynamic from 'next/dynamic';

// Hooks & Context
import useBreakpoint from '@/hooks/useBreakpoint';

import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';

const AtVideoPlayer = dynamic(
  () =>
    import('@/components/atoms/at-video-player/at-video-player').then(
      (mod) => ({ default: mod.AtVideoPlayer }),
    ),
  {
    ssr: false,
    loading: () => null,
  },
);

const AtClickable = dynamic(
  () =>
    import('@/components/atoms/at-clickable/at-clickable').then((mod) => ({
      default: mod.AtClickable,
    })),
  {
    loading: () => null,
  },
);

const AtIcon = dynamic(
  () =>
    import('@/components/atoms/at-icon/at-icon').then((mod) => ({
      default: mod.AtIcon,
    })),
  {
    loading: () => null,
  },
);

// Types & Enums
import { EImageIntents } from '@/components/atoms/at-media/at-media.types';
import type { IOrVideoHeroProps } from './or-video-hero.types';
//import { MlSearchLocationFormWrapper } from '../../molecules/ml-search-location-form-fuse/ml-search-location-form-wrapper';
import { useLocation } from '../../../context/locationContext';
import { MlSearchLocationFormFuse } from '../../molecules';

// Lazy load enums to reduce initial bundle
const loadEnums = () =>
  import('./or-video-hero.types').then((mod) => ({
    EIcons: mod.EIcons,
    EClickableSize: mod.EClickableSize,
    EClickableVariants: mod.EClickableVariants,
    ESearchLocationFormVariants: mod.ESearchLocationFormVariants,
  }));

// Constants
const SHORT_SCREEN_THRESHOLD = 720;
const RESIZE_DEBOUNCE_MS = 100;

/**
 * Renders a full-screen video hero with a fallback image, overlayed content, and a location search form.
 *
 * - Autoplays a background video using AtVideoPlayer unless motion should be reduced, there's an error, or user is on mobile.
 * - Applies an offset if the screen height is less than 720px to improve content visibility.
 * - Provides a manual play/pause toggle button for better accessibility and control.
 * - Includes a location search form, fed from context via useLocation().
 *
 */
export const OrVideoHero = ({
  description,
  image,
  title,
  videoUrl,
}: IOrVideoHeroProps): JSX.Element => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [isShortScreen, setIsShortScreen] = useState(false);
  const [showInteractiveElements, setShowInteractiveElements] = useState(false);
  const [enums, setEnums] = useState<any>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const { studioId, allLocations, onItemClick, allStudios, selectedText } =
    useLocation();

  const shouldReduceMotion = useReducedMotion();
  const breakpoint = useBreakpoint();

  const isMobile = useMemo(
    () => breakpoint === 'sm' || breakpoint === 'xs',
    [breakpoint],
  );
  const isVideoDisabled = useMemo(
    () => hasVideoError || shouldReduceMotion || isMobile,
    [hasVideoError, shouldReduceMotion, isMobile],
  );
  const shouldOffset = useMemo(
    () => isShortScreen && !isMobile,
    [isShortScreen, isMobile],
  );

  const handleResize = useCallback(() => {
    const isShort = window.innerHeight < SHORT_SCREEN_THRESHOLD;
    if (isShortScreen !== isShort) {
      startTransition(() => {
        setIsShortScreen(isShort);
      });
    }
  }, [isShortScreen]);

  const handlePlayPause = useCallback(() => {
    startTransition(() => {
      setIsPlaying((prev) => !prev);
    });
  }, []);

  const handleVideoError = useCallback(() => {
    if (!hasVideoError) {
      startTransition(() => {
        setHasVideoError(true);
      });
    }
  }, [hasVideoError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setShowInteractiveElements(true);
        loadEnums().then(setEnums);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, RESIZE_DEBOUNCE_MS);
    };

    handleResize();
    window.addEventListener('resize', debouncedResize, { passive: true });

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize]);

  const videoConfig = useMemo(
    () => ({
      file: {
        attributes: {
          style: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            objectFit: 'cover' as const,
          },
          playsInline: true,
          preload: 'metadata',
        },
      },
    }),
    [],
  );

  return (
    <div
      className={classNames(
        'relative h-full min-h-screen w-full',
        shouldOffset && 'pt-24',
      )}
    >
      <div className="absolute inset-0 -z-0" aria-hidden="true">
        {!isVideoDisabled && (
          <AtVideoPlayer
            loop
            muted
            playing={isPlaying}
            url={videoUrl}
            onReady={() => setIsVideoReady(true)}
            onError={handleVideoError}
            config={videoConfig}
          />
        )}

        {(isVideoDisabled || !isVideoReady) && (
          <AtMedia
            alt={image.alt}
            intent={EImageIntents.BACKGROUND}
            className={classNames(
              'absolute inset-0 transition-opacity duration-500',
              isVideoReady && !isVideoDisabled ? 'opacity-0' : 'opacity-100',
            )}
            sizes="100vw"
            roundedCorners="none"
            priority={true}
            quality={75}
            src={image.src}
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-video-overlay" />
      </div>

      <div className="container z-100 grid h-full min-h-screen grid-cols-12 items-center text-balance md:pl-10">
        <div className="col-span-12 py-25 text-left md:col-span-5 md:landscape:py-[calc(100%_-_12.5rem)] lg:landscape:py-[calc(100%_-_16.5rem)] xl:landscape:pb-6 xl:landscape:pt-[7rem]">
          <AtHeader
            element="h1"
            className="relative mb-3 text-skin-primary-inverted md:mb-18"
          >
            {title}
          </AtHeader>

          <AtText className="relative mb-9 text-skin-primary-inverted md:mb-12">
            {description}
          </AtText>

          {showInteractiveElements && (
            <MlSearchLocationFormFuse
              selectedText={selectedText}
              title="Explore plans available where you plan to build:"
              onElementClick={onItemClick}
              locationData={allLocations}
              studioData={allStudios}
              studioId={studioId}
              variant={enums?.ESearchLocationFormVariants?.HERO}
            />
          )}
        </div>
      </div>

      {!isVideoDisabled && showInteractiveElements && enums && (
        <div className="absolute bottom-4 left-4 z-0">
          <AtClickable
            ariaLabel={
              isPlaying ? 'Pause background video' : 'Play background video'
            }
            iconPosition="left"
            label={isPlaying ? 'Pause' : 'Play'}
            size={enums.EClickableSize.SM}
            variant={enums.EClickableVariants.PRIMARY}
            onClick={handlePlayPause}
            interactLocation="or-video-hero"
          >
            <AtIcon
              color="var(--color-button-text-primary)"
              icon={isPlaying ? enums.EIcons.PAUSE : enums.EIcons.PLAY}
              className="text-sm"
            />
          </AtClickable>
        </div>
      )}
    </div>
  );
};
